import type { EnhancedCard, EnhancedMarket } from 'lib/bff/types/feed.types';
import type {
    TeamInjuredPlayer,
    TeamLiveDataDetails,
    TeamStatCategory,
} from 'lib/bff/types/structuredTargetModal.types';
import type { ColorsPalette } from 'models/colorsPalette';
import type { EventVo } from 'models/event';
import type { MarketStatus, MarketVo } from 'models/market';
import type { MilestoneVo, SportsGame, StructuredTargetVo } from 'models/milestones';

import { toTitleCase, upperFirstLetter } from './caseing';
import type { ColorSchema } from './colors';
import { colorsPalettes, getColorSchemaFromPalette } from './colors';
import { getMarketCardImageTarget, resolveMarketImageUrl } from './marketRowImage';
import { getFullTeamName, getTeamNickname } from './teamNameParts';
import { toNflTeamPageSlug } from './teamPageSlug';

const COLOR_PALETTE_KEYS = ['Color Palette', 'color_palette', 'colors_palette', 'colorPalette'] as const;

const DYNAMIC_TARGET_ID_KEYS = [
    'football_team',
    'baseball_team',
    'basketball_team',
    'hockey_team',
    'soccer_team',
    'ufc_competitor',
    'home_team_id',
    'away_team_id',
] as const;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const asUnknownRecord = (value: unknown): Record<string, unknown> | undefined => {
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    return value as Record<string, unknown>;
};

const toDisplayLabel = (key: string): string =>
    key
        .replaceAll('_', ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const parseColorTokens = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((entry): entry is string => isNonEmptyString(entry)).map((entry) => entry.trim());
    }

    if (!isNonEmptyString(value)) {
        return [];
    }

    return value
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
};

export interface StructuredTargetColorSchema {
    primaryRgba: string;
    secondaryRgba: string;
}

const mapMarketStatus = (status: MarketStatus): EnhancedMarket['status'] | undefined => {
    if (status === 'active') {
        return 'open';
    }
    if (status === 'initialized' || status === 'inactive' || status === 'invalid') {
        return 'pending';
    }
    if (status === 'closed') {
        return 'closed';
    }
    if (status === 'settled') {
        return 'settled';
    }
    if (status === 'determined' || status === 'finalized' || status === 'amended' || status === 'disputed') {
        return 'determined';
    }
    return undefined;
};

const getStructuredTargetIdFromMarket = (market: MarketVo): string | undefined => {
    const marketWithStructuredTarget = market as MarketVo & { structured_target_id?: unknown };
    if (isNonEmptyString(marketWithStructuredTarget.structured_target_id)) {
        return marketWithStructuredTarget.structured_target_id;
    }

    const customStrike = asUnknownRecord(market.custom_strike);
    if (!customStrike) {
        return undefined;
    }

    for (const key of DYNAMIC_TARGET_ID_KEYS) {
        const value = customStrike[key];
        if (isNonEmptyString(value)) {
            return value;
        }
    }

    return undefined;
};

const toEnhancedMarket = (market: MarketVo, structuredTarget: StructuredTargetVo | undefined): EnhancedMarket => {
    const marketTitle = market.title || market.yes_sub_title || market.sub_title || market.ticker_name;
    const midpointPrice = Math.round((market.yes_bid + market.yes_ask) / 2);
    const price = Number.isFinite(market.last_price) ? market.last_price : midpointPrice;
    const previousPrice = market.previous_day_price ?? market.prev_period_price ?? price;
    const structuredTargetId = getStructuredTargetIdFromMarket(market);

    const marketStructuredTarget =
        structuredTargetId && structuredTarget && structuredTargetId === structuredTarget.id
            ? structuredTarget
            : undefined;

    const rulebookColorPalette = market.rulebook_variables?.['Color Palette'];
    const colorPalette =
        marketStructuredTarget?.product_details.color_palette ??
        (isNonEmptyString(rulebookColorPalette) ? rulebookColorPalette : undefined);
    const imageFromRulebook = market.rulebook_variables?.image_link;

    const structuredTargetImage = getStructuredTargetImageUrl(marketStructuredTarget);
    const resolvedImageUrl =
        structuredTargetImage ?? (isNonEmptyString(imageFromRulebook) ? imageFromRulebook : undefined);

    const enhancedMarket: EnhancedMarket = {
        title: marketTitle,
        ticker: market.ticker_name,
        yes_bid: market.yes_bid,
        yes_ask: market.yes_ask,
        no_bid: Math.max(0, 100 - market.yes_ask),
        no_ask: Math.max(0, 100 - market.yes_bid),
        price,
        previous_price: previousPrice,
        image_url: resolvedImageUrl,
        open_date: market.open_date,
        close_date: market.close_date,
        structured_target_id: structuredTargetId,
        structured_target: marketStructuredTarget,
        volume: market.volume,
        dollar_volume: market.dollar_volume,
        open_interest: market.open_interest,
        dollar_open_interest: market.dollar_open_interest,
        status: mapMarketStatus(market.status),
        result: market.result,
        yes_subtitle: market.yes_sub_title,
        color_palette: colorPalette,
    };

    return enhancedMarket;
};

export const transformTeamRouteType = (type: string): string => {
    return type.replaceAll('-', '_');
};

export const transformTeamRouteName = (name: string): string => {
    // Reverse of `slugifyTeamName`: `--` represents an original hyphen
    // (e.g. "Maccabi Tel-Aviv" ↔ "maccabi-tel--aviv"), while a single `-`
    // represents a word separator.
    return name.replaceAll('--', '\x00').replaceAll('-', ' ').replaceAll('\x00', '-');
};

/**
 * Candidate API queries to try for a given route slug, in priority order.
 *
 * The current encoder escapes literal hyphens as `--` (so the decoder can
 * reconstruct names like "Maccabi Tel-Aviv"). The previous encoder, whose
 * output is still committed to `public/sitemap-teams.xml`, instead stripped
 * any non-`[a-z0-9-]` character, so a name like "Brighton & Hove Albion"
 * collapsed into the same `brighton--hove-albion` shape — the modern
 * decoder would mistakenly hand that off as `brighton-hove albion` and
 * miss the real team.
 *
 * When the slug contains `--`, also produce a legacy candidate that
 * collapses every `-` to a space. The page tries the modern decoding first
 * and only falls through to the legacy form if the modern lookup misses,
 * so already-published sitemap URLs and external links keep resolving.
 *
 * `includeShorterPrefixes` additionally appends the word-prefixes of each
 * candidate, longest first — "new york giants" then "new york", then "new".
 * Leagues whose structured targets are named for the city alone (the NFL, whose
 * trademarked nicknames Kalshi can't carry in `name`) have no target matching
 * "New York Giants", so the only lookup that finds one is the city. Two teams
 * come back for it, and `selectStructuredTarget` then picks between them on the
 * full route slug, which it can match against the target's `market` + `team_name`
 * pair. Opt-in because a shorter query is a broader one: the artists page shares
 * this helper and wants an exact lookup or nothing.
 */
export const transformTeamRouteNameCandidates = (
    name: string,
    { includeShorterPrefixes = false }: { includeShorterPrefixes?: boolean } = {},
): string[] => {
    const modern = transformTeamRouteName(name);
    const legacy = name.replaceAll('-', ' ').replace(/\s+/g, ' ').trim();
    const exact = !name.includes('--') || legacy === modern ? [modern] : [modern, legacy];

    if (!includeShorterPrefixes) {
        return exact;
    }

    // Longest first, and only ever consulted when every longer candidate missed,
    // so a broader query can't pre-empt an exact one.
    const prefixes = exact.flatMap((candidate) => {
        const words = candidate.split(' ').filter(Boolean);
        return words.slice(1).map((_, index) => words.slice(0, words.length - 1 - index).join(' '));
    });

    return [...new Set([...exact, ...prefixes])].filter(Boolean);
};

export const transformTeamRouteParams = (params: { type: string; name: string }) => {
    return {
        transformedType: transformTeamRouteType(params.type),
        transformedName: transformTeamRouteName(params.name),
    };
};

const TEAM_STRUCTURED_TARGET_TYPES = new Set([
    'baseball_team',
    'basketball_team',
    'cricket_team',
    'football_team',
    'hockey_team',
    'lacrosse_team',
    'rugby_team',
    'soccer_team',
    'ufc_competitor',
]);

// Exported for the /artists/[name] route, which shares the same
// slug encoding (see utils/artists.ts).
export const slugifyTeamName = (value: string): string =>
    value
        .toLowerCase()
        .split('-')
        .map((segment) =>
            segment
                .replace(/[^a-z0-9\s]/g, '')
                .trim()
                .replace(/\s+/g, '-'),
        )
        .filter((segment) => segment.length > 0)
        .join('--');

/**
 * Returns the public Teams page href for a structured target, or `undefined`
 * if the target isn't a routable team type (e.g., `news_event`, `award`)
 * or is the "Tie" placeholder used for the draw side of 3-way sports
 * markets (it has no real team identity and shouldn't link out).
 *
 * The route at `/teams/[type]/[name]` re-derives the API params by replacing
 * dashes with underscores in the type and spaces in the name, so we mirror
 * that transform here using the structured target's `type` (`football_team`)
 * and human-readable `name` ("Dallas", "Kansas City").
 */
/**
 * `/nfl/teams/[team]` slug — re-exported from `utils/teamPageSlug`, which is where
 * it now lives so the NFL team directory can spell a slug this module's way without
 * importing this module. See that file for why the difference is worth a file.
 * Every existing import of it from here still resolves.
 */
export { toNflTeamPageSlug };

/**
 * Team-name normalization — re-exported from `utils/teamNameParts` for the same
 * reason `toNflTeamPageSlug` is: callers that already import this module shouldn't
 * grow a second import, and the leaf has to stay dependency-free. See that file for
 * why `details.team_name` can no longer be joined to `details.market` directly.
 */
export { getFullTeamName, getTeamNickname };

/**
 * Where a team's page lives.
 *
 * NFL teams are served only by `/nfl/teams/[team]` — the `/teams/[type]/[name]` URL
 * for them permanently redirects there — so linking them by that route would cost
 * every reader a redirect. Every other sport keeps the all-sports route, whose slug
 * comes from the target's own (trademark-safe) name.
 */
export const getStructuredTargetTeamPageHref = (
    structuredTarget: Pick<StructuredTargetVo, 'type' | 'name' | 'details'> | undefined,
): string | undefined => {
    if (!structuredTarget) return undefined;
    const { type, name } = structuredTarget;
    if (!isNonEmptyString(type) || !isNonEmptyString(name)) return undefined;
    if (!TEAM_STRUCTURED_TARGET_TYPES.has(type)) return undefined;
    if (name.trim().toLowerCase() === 'tie') return undefined;

    const details = asUnknownRecord(structuredTarget.details);
    if (toStringValue(details?.league)?.toUpperCase() === 'NFL') {
        // Both halves or nothing: the NFL route resolves `city-mascot` against a fixed
        // 32-team table, so a payload carrying only the city yields `/nfl/teams/kansas-city`
        // — a URL that route 404s on. Falling back to the all-sports URL costs a redirect;
        // guessing costs the page.
        //
        // The nickname has to be normalized, not read raw: `details.team_name` now
        // carries the market half ("GB Packers"), and joining that to `market` spells
        // `/nfl/teams/green-bay-gb-packers`, which is not in the 32-team table and so
        // 404s outright. See `utils/teamNameParts`.
        const market = toStringValue(details?.market);
        const teamName = getTeamNickname(details);
        if (market && teamName) {
            return `/nfl/teams/${toNflTeamPageSlug({ market, teamName })}`;
        }
    }

    const typeSlug = type.replaceAll('_', '-');
    const nameSlug = slugifyTeamName(name);
    if (!nameSlug) return undefined;

    return `/teams/${typeSlug}/${nameSlug}`;
};

// Maps a team structured-target type to its sport hub, mirroring the sport
// category routes used elsewhere (e.g. `/category/sports/football`). Drives the
// `/teams/*` breadcrumb trail so it lines up with the markets page hierarchy.
const TEAM_TYPE_TO_SPORT_CATEGORY: Record<string, { label: string; slug: string }> = {
    baseball_team: { label: 'Baseball', slug: 'baseball' },
    basketball_team: { label: 'Basketball', slug: 'basketball' },
    cricket_team: { label: 'Cricket', slug: 'cricket' },
    football_team: { label: 'Football', slug: 'football' },
    hockey_team: { label: 'Hockey', slug: 'hockey' },
    lacrosse_team: { label: 'Lacrosse', slug: 'lacrosse' },
    rugby_team: { label: 'Rugby', slug: 'rugby' },
    soccer_team: { label: 'Soccer', slug: 'soccer' },
    ufc_competitor: { label: 'MMA', slug: 'mma' },
};

export interface TeamBreadcrumb {
    label: string;
    /** Absent for the current page (the leaf team crumb). */
    href?: string;
}

/**
 * Builds the breadcrumb trail for a `/teams/[type]/[name]` page, mirroring the
 * markets page hierarchy: `Sports › [Sport] › [Team]`. The leaf team crumb has
 * no href (it's the current page).
 */
export const getTeamBreadcrumbTrail = (transformedType: string, teamName: string): TeamBreadcrumb[] => {
    const trail: TeamBreadcrumb[] = [{ label: 'Sports', href: '/sports/all-sports' }];

    const sport = TEAM_TYPE_TO_SPORT_CATEGORY[transformedType];
    if (sport) {
        trail.push({ label: sport.label, href: `/category/sports/${sport.slug}` });
    }

    const trimmedName = teamName.trim();
    if (trimmedName) {
        trail.push({ label: trimmedName });
    }

    return trail;
};

export const getStructuredTargetColorSchema = (
    structuredTarget?: StructuredTargetVo,
    isDarkMode?: boolean,
): ColorSchema | null => {
    if (!structuredTarget) {
        return null;
    }

    const details = asUnknownRecord(structuredTarget.details);
    const productDetails = asUnknownRecord(structuredTarget.product_details);
    const nestedProductDetails = asUnknownRecord(details?.product_details);

    const sources = [details, productDetails, nestedProductDetails];

    const colors = new Set<string>();

    for (const source of sources) {
        if (!source) {
            continue;
        }

        for (const key of COLOR_PALETTE_KEYS) {
            const colorTokens = parseColorTokens(source[key]);
            colorTokens.forEach((token) => colors.add(token));
        }
    }

    if (colors.size === 0) {
        return null;
    }

    const paletteName = Array.from(colors)[0];
    const colorPalette = colorsPalettes[paletteName] as unknown as ColorsPalette;
    const colorSchema = getColorSchemaFromPalette(colorPalette, isDarkMode);

    return colorSchema;
};

/** A team brand color as a light/dark pair, so the theme is picked in CSS rather than at render. */
export interface TeamColorPair {
    light: string;
    dark: string;
}

/**
 * Resolve a team's brand color from its structured-target palette as a light/dark
 * pair. Returns null when no palette is set, so callers fall back to their default;
 * a palette that resolves for only one theme is mirrored across both.
 */
export const resolveTeamColorPair = (structuredTarget?: StructuredTargetVo): TeamColorPair | null => {
    const light = getStructuredTargetColorSchema(structuredTarget, false)?.primaryRgba;
    const dark = getStructuredTargetColorSchema(structuredTarget, true)?.primaryRgba;
    const resolvedLight = light || dark;
    const resolvedDark = dark || light;
    if (!resolvedLight || !resolvedDark) {
        return null;
    }

    return { light: resolvedLight, dark: resolvedDark };
};

export const getStructuredTargetImageUrl = (structuredTarget?: StructuredTargetVo): string | undefined => {
    const imageUrl = structuredTarget?.product_details?.image_url;
    return isNonEmptyString(imageUrl) ? imageUrl : undefined;
};

export { getMarketImageTarget as pickCustomStrikeImageTarget } from './marketRowImage';

/** Conventional S3 base path for hub team logos, named `${competition}_${teamKey}.webp`. */
const TEAM_LOGO_BASE_URL = 'https://kalshi-public-docs.s3.us-east-1.amazonaws.com/structured_targets';

export const teamLogoUrlForKey = (competition: string, teamKey: string): string =>
    `${TEAM_LOGO_BASE_URL}/${competition}_${teamKey}.webp`;

export type LogoResolverInput = {
    image_url?: string;
    image_url_dark_mode?: string;
    image_url_light_mode?: string;
    associated_structured_targets?: Record<string, string>;
    structured_target_id?: string;
    structured_target?: StructuredTargetVo;
    /**
     * True when this option/market is the tie/draw side of a 3-way sports game.
     * The Tie's structured_target is a `soccer_team` named "Tie" that carries a generic
     * rulebook icon.
     */
    isTie?: boolean;
};

/** The market's own artwork: its structured target's image, else `image_url`.
 * `targets` (a card's hydrated `structured_targets`) resolves markets that
 * carry only a `structured_target_id` or `associated_structured_targets`. */
export const resolveMarketLogoUrl = (
    market: LogoResolverInput | undefined,
    targets?: Record<string, StructuredTargetVo>,
): string | undefined => {
    if (!market) return undefined;

    return resolveMarketImageUrl(getMarketCardImageTarget(market, targets), market.image_url);
};

/**
 * Returns `info` with the image resolved from its paired market option.
 *
 * Sport body layouts get names and scores from live game data, but some feeds
 * (notably tennis) only provide competitor images on the market option. Resolve
 * the option image here so every odds layout uses the same source as the
 * prediction layout.
 */
export const withResolvedMarketImage = <T extends { imageUrl?: string }>(
    info: T,
    market: LogoResolverInput | undefined,
): T => {
    const imageUrl = resolveMarketLogoUrl(market);

    if (!imageUrl) return info;
    return { ...info, imageUrl };
};

export const getSportsGameImageUrl = (sportsGame?: SportsGame, isHome?: boolean): string => {
    if (!sportsGame) {
        return '';
    }

    const imageUrl = isHome ? sportsGame.home_image_url : sportsGame.away_image_url;
    return isNonEmptyString(imageUrl) ? imageUrl : '';
};

export const formatStructuredTargetTypeLabel = (type: string): string => toDisplayLabel(type.replaceAll('_', ' '));

export interface StructuredTargetMetadataRow {
    label: string;
    value: string;
}

/**
 * Conference and division names that are acronyms rather than words, so they stay
 * fully capitalized. The payload writes these in caps ("AFC", "AFC EAST"), and
 * sentence-casing them blindly turned the acronym into a word — "Afc east".
 * Anything not listed is a real word ("Eastern", "Metropolitan", "Atlantic") and is
 * capitalized normally.
 */
const CONFERENCE_ACRONYMS = new Set(['AFC', 'NFC', 'AL', 'NL', 'ACC', 'SEC', 'MLS', 'NBA', 'NFL', 'MLB', 'NHL']);

/**
 * "AFC EAST" → "AFC East", "EASTERN" → "Eastern": each word capitalized, with the
 * league acronyms above left in caps.
 */
export const formatConferenceOrDivisionName = (name: string): string =>
    name
        .trim()
        .split(/\s+/)
        .map((word) =>
            CONFERENCE_ACRONYMS.has(word.toUpperCase()) ? word.toUpperCase() : upperFirstLetter(word.toLowerCase()),
        )
        .join(' ');

export const getStructuredTargetMetadataRows = (
    structuredTarget?: StructuredTargetVo,
    maxRows: number = 15,
): StructuredTargetMetadataRow[] => {
    if (!structuredTarget) {
        return [];
    }

    const details = asUnknownRecord(structuredTarget.details);

    const rows: StructuredTargetMetadataRow[] = [];
    const seenLabels = new Set<string>();

    const pushRow = (label: string, value: unknown) => {
        if (rows.length >= maxRows) {
            return;
        }
        if (!isNonEmptyString(value)) {
            return;
        }
        if (seenLabels.has(label)) {
            return;
        }

        rows.push({ label, value: value.trim() });
        seenLabels.add(label);
    };

    // Season standigns
    const seasonStandings = Array.isArray(details?.season_standings) ? details.season_standings[0] : undefined;

    if (seasonStandings) {
        // Rank
        const rankRecord = asRecord(seasonStandings.rank);

        // pushRow('Abbreviation', details?.abbreviation);

        // Conference
        const conferenceRank = toStringValue(rankRecord?.conference);
        const conferenceRecord = formatRecord(seasonStandings.conference_wins, seasonStandings.conference_losses);
        if (details?.conference) {
            const parts = [formatConferenceOrDivisionName(details.conference as string)];
            if (conferenceRank) parts.push(`Rank #${conferenceRank}`);
            if (conferenceRecord) parts.push(`Record ${conferenceRecord}`);
            pushRow('Conference', parts.join(' · '));
        }

        // Division
        const divisionRank = toStringValue(rankRecord?.division);
        const divisionRecord = formatRecord(seasonStandings.division_wins, seasonStandings.division_losses);
        if (details?.division) {
            const parts = [formatConferenceOrDivisionName(details.division as string)];
            if (divisionRank) parts.push(`Rank #${divisionRank}`);
            if (divisionRecord) parts.push(`Record ${divisionRecord}`);
            pushRow('Division', parts.join(' · '));
        }

        // Season
        const year = toStringValue(seasonStandings.year);
        const seasonType = toStringValue(seasonStandings.season_type);
        const seasonRecord = formatRecord(seasonStandings.wins, seasonStandings.losses);
        const seasonParts: string[] = [];
        if (seasonType && year) {
            seasonParts.push(`${toTitleCase(seasonType)} ${year}`);
        } else if (year) {
            seasonParts.push(year);
        } else if (seasonType) {
            seasonParts.push(toTitleCase(seasonType));
        }
        if (seasonRecord) seasonParts.push(`Record ${seasonRecord}`);
        if (seasonParts.length > 0) {
            pushRow('Season', seasonParts.join(' · '));
        }
    }

    pushRow('Country', details?.country);
    // pushRow('League', details?.league);

    // Pad to an even number of rows for better aesthetics
    if (rows.length % 2 !== 0) {
        rows.push({ label: '', value: '' });
    }

    return rows;
};

export const toNumberValue = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsedNumber = Number(value);
        return Number.isFinite(parsedNumber) ? parsedNumber : undefined;
    }
    return undefined;
};

export const asRecord = (value: unknown): Record<string, unknown> | undefined => {
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    return value as Record<string, unknown>;
};

export const toStringValue = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        return trimmedValue.length > 0 ? trimmedValue : undefined;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : undefined;
    }
    return undefined;
};

export const formatRecord = (wins: unknown, losses: unknown): string | undefined => {
    const winsNumber = toNumberValue(wins);
    const lossesNumber = toNumberValue(losses);
    if (winsNumber === undefined || lossesNumber === undefined) {
        return undefined;
    }

    return `${winsNumber}-${lossesNumber}`;
};

export const formatStatValue = (value: unknown): string | undefined => {
    const numericValue = toNumberValue(value);
    if (numericValue !== undefined) {
        return Number.isInteger(numericValue) ? `${numericValue}` : numericValue.toFixed(2).replace(/\.?0+$/, '');
    }

    return toStringValue(value);
};

export const convertEventsToEnhancedCards = (
    events: EventVo[],
    structuredTarget: StructuredTargetVo | undefined,
    milestones: Record<string, MilestoneVo> | undefined,
): EnhancedCard[] => {
    const sorted = [...events].sort((a, b) => {
        const dateA = a.target_datetime ? new Date(a.target_datetime).getTime() : 0;
        const dateB = b.target_datetime ? new Date(b.target_datetime).getTime() : 0;
        return dateB - dateA;
    });

    return sorted.map((event) => {
        const milestoneId = event.product_metadata?.promoted_milestone_id;
        const milestone = milestoneId ? milestones?.[milestoneId] : undefined;
        const markets = (event.markets ?? []).map((market) => toEnhancedMarket(market, structuredTarget));

        const totalEventVolume = markets.reduce((accumulator, market) => accumulator + (market.volume ?? 0), 0);
        const card: EnhancedCard = {
            series_ticker: event.series_ticker,
            series_title: event.mini_title || event.title,
            event_ticker: event.ticker,
            event_subtitle: event.sub_title || '',
            event_title: event.title,
            general_title_super: event.product_metadata?.competition,
            total_series_volume: totalEventVolume,
            total_event_volume: totalEventVolume,
            product_metadata: {
                ...event.product_metadata,
                custom_image_url:
                    event.product_metadata?.custom_image_url ?? getStructuredTargetImageUrl(structuredTarget),
            },
            category: event.category,
            markets,
        };
        if (milestone) {
            card.milestone_id = milestoneId;
            card.milestone = milestone;
            card.start_date = milestone.start_date;
        }
        return card;
    });
};

export function isCardLive(card: EnhancedCard): boolean {
    return card.markets?.some((m) => m.status === 'open') ?? false;
}

export function splitCardsByLiveStatus(cards: EnhancedCard[]): { live: EnhancedCard[]; upcoming: EnhancedCard[] } {
    const live: EnhancedCard[] = [];
    const upcoming: EnhancedCard[] = [];
    for (const card of cards) {
        if (isCardLive(card)) {
            live.push(card);
        } else {
            upcoming.push(card);
        }
    }
    return { live, upcoming };
}

// ── Shared sportradar stat/injury display helpers ─────────────────────────────
// Used by both the hub team modal (app/(explore)/…/team-modal) and the event
// page's sports stats modal (react_pages/Event/…/Sports).

export function statCategoryOf(source: Record<string, unknown> | undefined, category: string): TeamStatCategory {
    const value = source?.[category];
    return value && typeof value === 'object' ? (value as TeamStatCategory) : {};
}

export type StatFormat = 'int' | 'decimal' | 'pct';

export function formatStat(value: number | string | undefined, format: StatFormat = 'int'): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    if (numeric === undefined || !Number.isFinite(numeric)) return '–';
    if (format === 'decimal') return numeric.toLocaleString('en-US', { maximumFractionDigits: 1 });
    if (format === 'pct') return `${numeric.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
    return Math.round(numeric).toLocaleString('en-US');
}

/** "Josh Allen" → "J. Allen"; single-word names pass through. */
export function abbreviateName(name: string | undefined): string {
    const trimmed = name?.trim() ?? '';
    const spaceIndex = trimmed.indexOf(' ');
    if (spaceIndex <= 0) return trimmed;
    return `${trimmed[0]}. ${trimmed.slice(spaceIndex + 1)}`;
}

export interface InjuryReportRow {
    key: string;
    name: string;
    /** Jersey number as the feed carries it ("15", "00"); absent when blank. */
    jersey?: string;
    position?: string;
    /** Injury status, e.g. "Questionable". */
    status?: string;
    /** What's injured, e.g. "Hand". */
    detail?: string;
}

/**
 * Normalize the injury slice into display rows. The demo BFF returns
 * `injuries: null` today, so both feed variants (report object / bare player
 * list) are accepted and unreadable entries drop out.
 */
export function injuryReportRows(details: TeamLiveDataDetails | undefined): InjuryReportRow[] {
    const raw = details?.injuries;
    const players: TeamInjuredPlayer[] = Array.isArray(raw) ? raw : (raw?.players ?? []);
    return players.flatMap((player) => {
        const name = player.name ?? player.abbr_name;
        if (!name) return [];
        const injury = player.injuries?.[0];
        return [
            {
                key: player.id ?? player.sr_id ?? name,
                name,
                jersey: player.jersey?.trim() || undefined,
                position: player.position,
                status: injury?.status ?? player.status,
                detail: injury?.primary ?? injury?.desc ?? injury?.injury,
            },
        ];
    });
}
